import { useState, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import type { CustomerVendor } from '@/resources/leasing'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'

import queries from './queries'
import { ecomPolicyResources } from './resources'

export type VerifyCategoriesByLeasingVendor = {
  isLeasing: boolean
  leasingVendorId?: number | null
  vendors: CustomerVendor
}

export const useEverSelectedCategories = (
  selectedCategoryIds: number[] | null
): [number[] | null, (v: number[] | null) => void] => {
  const [everSelectedCategoryIds, setEverSelectedCategoryIds] = useState<number[] | null>(null)

  useEffect(() => {
    if (!selectedCategoryIds) return

    selectedCategoryIds?.forEach(id => {
      if (!everSelectedCategoryIds) {
        setEverSelectedCategoryIds([id])
        return
      }

      if (!everSelectedCategoryIds.includes(id)) {
        setEverSelectedCategoryIds([...everSelectedCategoryIds, id])
      }
    })
  }, [everSelectedCategoryIds, selectedCategoryIds])

  return [everSelectedCategoryIds, setEverSelectedCategoryIds]
}

export const useCorrectCategoryClassifications = (policyId: number) => {
  const [leasingVendorCategories, setLeasingVendorCategories] = useState<string[]>([])
  const [removedCategories, setRemovedCategories] = useState<string[]>([])
  const [leasingOrderIntervals, setLeasingOrderIntervals] = useState<number[]>([])

  const { data: policy, isLoading: isLoadingPolicy } = useQuery(
    queries.policyDetails(policyId),
    () => ecomPolicyResources.getPolicyDetails(policyId)
  )
  const { data: categoryClassification, isLoading: isLoadingCategoryClassification } = useQuery(
    queries.categoryClassification(),
    ecomPolicyResources.getCategoryClassification
  )
  const { data: customerVendors, isLoading: isLoadingCustomerVendors } = useQuery(
    commonQueries.customerVendorsList(),
    leasingResources.getCustomerVendors
  )

  const findLeasingVendor = (leasingVendorId?: number | null, vendors = customerVendors) =>
    vendors?.find(
      v =>
        v.vendorId === leasingVendorId &&
        (policy?.isGlobal
          ? v.usePolicyLevelLeasing
          : v.customerId === policy?.customerId && v.usePolicyLevelLeasing)
    )

  const getLeasingVendorCategories = () => {
    if (!policy || !customerVendors) return []
    const leasingVendor = findLeasingVendor(policy.leasingVendorId, customerVendors)
    return leasingVendor?.productCategories || []
  }

  const verifyCategoriesByLeasingVendor = ({
    isLeasing,
    leasingVendorId,
    vendors,
  }: VerifyCategoriesByLeasingVendor) => {
    const leasingVendor =
      policy && categoryClassification && isLeasing && leasingVendorId
        ? findLeasingVendor(leasingVendorId, vendors)
        : undefined

    if (
      policy &&
      categoryClassification &&
      leasingVendor &&
      leasingVendor.productCategories.length > 0
    ) {
      const vendorCategories = leasingVendor.productCategories.map(({ productType }) => productType)
      setLeasingVendorCategories(vendorCategories)

      const selected = policy.classificationList.split(',').map(c => c.trim())
      const removedPolicyCategories = categoryClassification
        .filter(c => selected.includes(c.id) && !vendorCategories.includes(c.id))
        .map(c => c.name)
      setRemovedCategories(removedPolicyCategories)

      return {
        showModal: removedPolicyCategories.length > 0,
        leasingVendorCategories: vendorCategories,
        removedPolicyCategories,
      }
    }

    setLeasingVendorCategories([])
    setRemovedCategories([])

    return {
      showModal: false,
      leasingVendorCategories: [],
      removedPolicyCategories: [],
    }
  }

  const verifyLeasingCompatibility = () => {
    if (policy && customerVendors) {
      const categoriesResult = verifyCategoriesByLeasingVendor({
        isLeasing: policy.isLeasing,
        leasingVendorId: policy.leasingVendorId,
        vendors: customerVendors,
      })

      const intervalResult = verifyLeasingOrderIntervals({
        isLeasing: policy.isLeasing,
        leasingVendorId: policy.leasingVendorId,
        vendors: customerVendors,
      })
      return { categoriesResult, intervalResult }
    }

    setLeasingVendorCategories([])
    setRemovedCategories([])
    setLeasingOrderIntervals([])
    return {
      categoriesResult: {
        showModal: false,
        leasingVendorCategories: [],
        removedPolicyCategories: [],
      },
      intervalResult: {
        showModal: false,
        leasingOrderIntervals: [],
      },
    }
  }

  const verifyLeasingOrderIntervals = ({
    isLeasing,
    leasingVendorId,
    vendors,
  }: VerifyCategoriesByLeasingVendor) => {
    const leasingVendor =
      policy && vendors && isLeasing && leasingVendorId
        ? findLeasingVendor(leasingVendorId, vendors)
        : undefined

    if (
      policy &&
      leasingVendor &&
      !policy.lockFields?.includes('order_interval') &&
      leasingVendor.productCategories.length > 0
    ) {
      const selected = policy.classificationList.split(',').map(c => c.trim())
      const relevantCategories = leasingVendor.productCategories.filter(({ productType }) =>
        selected.includes(productType)
      )
      const durations =
        relevantCategories.length > 0 ? relevantCategories : leasingVendor.productCategories

      const sortedIntervals = [
        ...new Set(durations.map(({ monthDuration }) => monthDuration)),
      ].sort((a, b) => a - b)
      setLeasingOrderIntervals(sortedIntervals)

      const isValid = sortedIntervals.length === 0 || sortedIntervals.includes(policy.orderInterval)
      return {
        showModal: !isValid,
        leasingOrderIntervals: sortedIntervals,
      }
    }

    setLeasingOrderIntervals([])
    return {
      showModal: false,
      leasingOrderIntervals: undefined,
    }
  }

  return {
    isLoadingCategories:
      isLoadingPolicy || isLoadingCategoryClassification || isLoadingCustomerVendors,
    isLoadingPolicyOrCustomerVendors: isLoadingPolicy || isLoadingCustomerVendors,
    leasingVendorCategories,
    removedCategories,
    leasingOrderIntervals,
    leasingVendorProductCategories: getLeasingVendorCategories(),
    verifyLeasingCompatibility,
    verifyCategoriesByLeasingVendor,
    verifyLeasingOrderIntervals,
  }
}
